import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { useGrants, useDisplayName } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import { useZodForm } from '../../lib/forms/useZodForm'
import { minutesFormSchema, type MinutesFormValues } from './schema'
import MeetingInfoSection from './components/MeetingInfoSection'
import ParticipantsSection from './components/ParticipantsSection'
import DocumentsSection from './components/DocumentsSection'
import DeferredSection from './components/DeferredSection'
import ExcelImportSection from './components/ExcelImportSection'
import ExcelImportPreviewDialog from './components/ExcelImportPreviewDialog'
import TutanakPreviewDialog, { type PreviewData } from './components/TutanakPreviewDialog'
import { useMeetingGroupOptions } from './hooks/useMeetingGroupOptions'
import { useMeetingContentSections } from '../meetings/hooks/useMeetingContentSections'
import { contentSectionTitle } from '../meetings/types'
import ActionsEditor from '../actions/components/ActionsEditor'
import { useActionLevels } from '../actions/hooks/useActionLevels'
import { ACTION_DECISION_TYPE_ID, buildActionPayload, type ActionFormRow } from '../actions/types'
import MinuteItemsEditor from '../minuteItems/MinuteItemsEditor'
import {
  KIND_BY_DECISION_TYPE,
  buildMinuteItemsPayload,
  hasDescription,
  type MinuteItemInput,
  type MinuteItemKind,
  type MinuteItemRow,
} from '../minuteItems/types'
import { useCreateMinutes } from './hooks/useCreateMinutes'
import { useExcelImport } from './hooks/useExcelImport'
import {
  downloadExcelTemplate,
  downloadMinutesPreviewPdf,
  recordMinutesCreateScreenAccess,
  uploadMinutesAttachments,
} from './api/minutesApi'
import { uploadPrincipleAttachments } from '../principleDecisions/api/principleDecisionApi'
import { planPendingPrincipleUploads } from '../minuteItems/pendingUploads'
import {
  excelRowsToActionRows,
  excelRowsToItemRows,
  ITEM_KIND_BY_SHEET,
  replaceExcelRows,
} from './excelImport'
import { getApiErrorMessage } from '../../api/apiError'
import type { EmployeePick, ExcelImportPreview, MinutesCreateRequest } from './types'
import { toPersonInput } from './personInput'
import './minutes.css'

/** "YYYY-MM-DD" → "dd.MM.yyyy" (önizleme gösterimi). */
function formatDate(iso: string): string {
  const [y, m, d] = (iso || '').split('-')
  return y && m && d ? `${d}.${m}.${y}` : iso
}

/**
 * Tutanak Oluştur ekranı (039/044). Temel bilgiler + katılımcılar + dokümanlar girilir; <b>Yayınla</b> önce
 * bir <b>önizleme (PDF)</b> açar; oradaki Yayınla ile toplantı + katılımcılar oluşturulur, dokümanlar iliştirilir
 * ve standart tutanak PDF'i üretilip eklenir (Toplantı Detay'dan indirilebilir). Önizlemeden İndir ile PDF
 * kaydetmeden indirilir. Gündem/Prensip/Karar/Aksiyon bölümleri görünür-pasif. Yetki: MINUTES_CREATE.
 */
export default function MinutesCreatePage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const { hasGrant, isLoading: grantsLoading } = useGrants()
  const editorName = useDisplayName() ?? ''
  const groupOptions = useMeetingGroupOptions()
  const sections = useMeetingContentSections()

  const { control, handleSubmit } = useZodForm(minutesFormSchema, {
    defaultValues: {
      meetingGroupId: null,
      name: '',
      meetingDate: '',
      meetingStartTime: '',
      meetingEndTime: '',
      meetingLocation: '',
    },
  })
  const [participants, setParticipants] = useState<EmployeePick[]>([])
  const [participantError, setParticipantError] = useState<string | null>(null)
  // Dağıtım listesi (119) — opsiyonel; kişiler toplantıyı görmez, yayında PDF'li e-posta alır.
  const [distributionList, setDistributionList] = useState<EmployeePick[]>([])
  const [actionRows, setActionRows] = useState<ActionFormRow[]>([])
  // Basit içerik satırları (Gündem/Prensip/Karar) — kind bazlı yerel durum.
  const [itemRows, setItemRows] = useState<Record<MinuteItemKind, MinuteItemRow[]>>({
    agenda: [],
    principles: [],
    decisions: [],
  })
  const setKindRows = (kind: MinuteItemKind, rows: MinuteItemRow[]) =>
    setItemRows((prev) => ({ ...prev, [kind]: rows }))
  const actionLevels = useActionLevels()
  const meetingDateVal = useWatch({ control, name: 'meetingDate' })
  const [files, setFiles] = useState<File[]>([])
  const [publishing, setPublishing] = useState(false)
  const [downloading, setDownloading] = useState(false)
  // Önizleme: Yayınla'da açılır; onaylı değerler burada tutulur (dialog'daki Yayınla/İndir bunları kullanır).
  const [pending, setPending] = useState<MinutesFormValues | null>(null)
  const create = useCreateMinutes()

  // ── 088 Excel ile toplu yükleme ──
  const excelImport = useExcelImport()
  const [excelPreview, setExcelPreview] = useState<ExcelImportPreview | null>(null)
  const [templateDownloading, setTemplateDownloading] = useState(false)
  const hasExcelRows =
    actionRows.some((r) => r.source === 'excel') ||
    Object.values(itemRows).some((rows) => rows.some((r) => r.source === 'excel'))

  /**
   * Ekran erişimi denetim izi (kod 500) — ekran açıldığında **tam bir kez** yazılır.
   *
   * <p>Boş bağımlılık dizisi bilinçli: olay "ekranın açılması"dır, bir durumun değişmesi değil.
   *
   * <p><b>`ref` muhafızı ZORUNLU (StrictMode):</b> React geliştirme modunda effect'leri iki kez
   * çağırır (mount → effect → cleanup → effect) ve bu effect **idempotent değildir** — her çağrı
   * `MEETING_LOG`'a bir satır yazar. Muhafız olmadan tek ekran açılışı **iki** denetim satırı
   * üretiyordu (2026-08-24'te canlı denemede görüldü: 0.12 sn arayla iki kayıt). Üretim build'inde
   * çift çağrı olmaz, ama "prod'da olmuyor" bir çözüm değil: kural "bir kullanıcı eylemi = bir
   * satır"dır (093/FR-013) ve geliştirme ortamında da geçerli olmalı. `ref` aynı bileşen örneğinde
   * korunduğu için çift çağrıyı güvenilir biçimde tekler.
   *
   * <p>`await` edilmez ve hata yutulur: denetim izi ekranın açılmasını engellememelidir.
   */
  const screenAccessLogged = useRef(false)
  useEffect(() => {
    if (screenAccessLogged.current) return
    screenAccessLogged.current = true
    recordMinutesCreateScreenAccess()
  }, [])

  /**
   * Aktarılmamış ön izleme varken sekmeyi kapatma/yenileme **tarayıcı uyarısı** tetikler (spec Edge
   * Cases): ön izleme sunucuda saklanmadığı için çıkışta kaybolur.
   *
   * <p><b>Kapsam sınırı:</b> yalnız tarayıcı düzeyi gezinme (kapat/yenile/adres değiştir) kapsanır.
   * Uygulama içi rota değişimi için router engelleyicisi gerekir; ekranın form verisi için de bugün
   * **hiçbir** kaydedilmemiş-veri uyarısı yok (analiz 1.4.1'in o maddesi 039'da uygulanmamış). O boşluk
   * bu işin kapsamı değil; burada yalnız Excel ön izlemesi korunuyor.
   */
  useEffect(() => {
    if (excelPreview == null) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [excelPreview])

  const onDownloadTemplate = async () => {
    setTemplateDownloading(true)
    try {
      await downloadExcelTemplate()
    } catch (err) {
      toast.error(getApiErrorMessage(err) ?? t('minutes.excel.uploadFailed'))
    } finally {
      setTemplateDownloading(false)
    }
  }

  const onExcelFileSelected = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error(t('minutes.excel.onlyXlsx'))
      return
    }
    excelImport.mutate(
      // Toplantı tarihi girilmişse hedef teslim tarihi alt sınırı max(toplantı, bugün) olur (FR-028).
      { file, meetingStartDate: meetingDateVal ?? null },
      {
        onSuccess: setExcelPreview,
        onError: (err) => toast.error(getApiErrorMessage(err) ?? t('minutes.excel.uploadFailed')),
      },
    )
  }

  /**
   * Aktarım: yalnız **Excel kaynaklı** satırların yerine geçer, elle girilenlere dokunulmaz (FR-022).
   * Kalıcılaşma yine Yayınla/Kaydet ile olur — bu adım hiçbir kayıt yazmaz.
   */
  const onExcelTransfer = () => {
    if (!excelPreview) return
    let transferred = 0
    for (const sheet of excelPreview.sheets) {
      if (sheet.kind === 'ACTION') {
        const next = excelRowsToActionRows(sheet.rows)
        transferred += next.length
        setActionRows((prev) => replaceExcelRows(prev, next))
      } else {
        const kind = ITEM_KIND_BY_SHEET[sheet.kind]
        const next = excelRowsToItemRows(sheet.rows, kind)
        transferred += next.length
        setItemRows((prev) => ({ ...prev, [kind]: replaceExcelRows(prev[kind], next) }))
      }
    }
    setExcelPreview(null)
    toast.success(t('minutes.excel.transferred', { count: transferred }))
  }

  const handleParticipantsChange = (next: EmployeePick[]) => {
    setParticipants(next)
    if (next.length > 0 && participantError) setParticipantError(null)
  }

  if (!grantsLoading && !hasGrant(GRANTS.MINUTES_CREATE)) {
    return (
      <div className="page minutes-page">
        <EmptyState
          icon="pi pi-lock"
          title={t('minutes.forbidden.title')}
          description={t('minutes.forbidden.desc')}
        />
      </div>
    )
  }

  // Basit içerik payload'ları (Gündem/Prensip/Karar); herhangi biri eksik-alanlı satır içerirse 'invalid'.
  const buildItemPayloads = ():
    | { agenda: MinuteItemInput[]; principles: MinuteItemInput[]; decisions: MinuteItemInput[] }
    | 'invalid' => {
    const agenda = buildMinuteItemsPayload(itemRows.agenda, hasDescription('agenda'))
    const principles = buildMinuteItemsPayload(itemRows.principles, hasDescription('principles'))
    const decisions = buildMinuteItemsPayload(itemRows.decisions, hasDescription('decisions'))
    if (agenda === 'invalid' || principles === 'invalid' || decisions === 'invalid')
      return 'invalid'
    return { agenda, principles, decisions }
  }

  // Ortak gönderim öncesi doğrulama: katılımcı + aksiyon + basit içerik. Hata → toast/işaret, false döner.
  const validateContents = (): boolean => {
    if (participants.length === 0) {
      setParticipantError(t('minutes.participants.required'))
      return false
    }
    if (buildActionPayload(actionRows) === 'invalid') {
      toast.error(t('actions.editor.invalid'))
      return false
    }
    if (buildItemPayloads() === 'invalid') {
      toast.error(t('minuteItems.common.invalid'))
      return false
    }
    setParticipantError(null)
    return true
  }

  // Yayınla → doğrula → önizlemeyi aç (henüz kaydetme yok).
  const onValid = (v: MinutesFormValues) => {
    if (!validateContents()) return
    setPending(v)
  }

  const buildBody = (v: MinutesFormValues, publish: boolean): MinutesCreateRequest => ({
    meetingGroupId: v.meetingGroupId as number,
    name: v.name,
    // Tarih (YYYY-MM-DD) + başlangıç/bitiş saati (SS:DD) → ayrı LocalDateTime'lar.
    meetingDate: `${v.meetingDate}T${v.meetingStartTime}:00`,
    meetingEndDate: `${v.meetingDate}T${v.meetingEndTime}:00`,
    meetingLocation: v.meetingLocation,
    participants: participants.map(toPersonInput),
    publish,
    // Aksiyonlar (gönderim öncesi doğrulandı; savunma amaçlı invalid → []).
    actions: (() => {
      const ap = buildActionPayload(actionRows)
      return ap === 'invalid' ? [] : ap
    })(),
    // Gündem/Prensip/Karar (savunma amaçlı invalid → boş listeler).
    ...(() => {
      const items = buildItemPayloads()
      return items === 'invalid' ? { agenda: [], principles: [], decisions: [] } : items
    })(),
    distributionList: distributionList.map(toPersonInput),
  })

  /**
   * Prensip kararı satırlarında bekleyen dosyaları, kayıttan dönen id'lerle yükler (108).
   *
   * <p>Eşlemenin kendisi saf bir fonksiyondadır ({@link planPendingPrincipleUploads}) — asıl risk
   * (indis kayması) orada, testlenebilir hâlde durur. Burada yalnız ağ ve sonucun raporlanması var.
   *
   * @return dosyaların bir kısmı **yüklenemediyse** true; çağıran kullanıcıyı uyarır (tutanak yine
   *     kaydedilmiştir). Eşleşmeyen satır da başarısızlık sayılır: sessiz kalmak, "kaydedildi" deyip
   *     dosyaları yok etmek demekti.
   */
  const uploadPendingPrincipleFiles = async (principleIds: number[] | undefined): Promise<boolean> => {
    const plan = planPendingPrincipleUploads(itemRows.principles, principleIds)
    let failed = plan.unmatched
    for (const { principleId, files: pending } of plan.uploads) {
      try {
        await uploadPrincipleAttachments(principleId, pending)
      } catch {
        failed = true
      }
    }
    return failed
  }

  // create.mutate + dosya yükleme + toast + reset — Kaydet (taslak) ve Yayınla ortak.
  const submitCreate = (v: MinutesFormValues, publish: boolean) => {
    setPublishing(true)
    create.mutate(buildBody(v, publish), {
      onSuccess: async (res) => {
        let filePartial = false
        if (files.length > 0) {
          try {
            await uploadMinutesAttachments(res.meetingId, files)
          } catch {
            filePartial = true
          }
        }
        const principleFilesFailed = await uploadPendingPrincipleFiles(res.principleIds)
        toast.success(publish ? t('minutes.toast.published') : t('minutes.toast.savedDraft'))
        if (filePartial) toast.error(t('minutes.toast.filePartial'))
        if (principleFilesFailed) toast.error(t('minuteItems.files.uploadPartial'))
        // Kaydet/Yayınla sonrası oluşturulan toplantının detayına yönlendir (form sıfırlama unmount ile).
        navigate(`/minutes/meeting-minutes/meetings/${res.meetingId}`)
      },
      onError: () => setPublishing(false),
    })
  }

  // Kaydet → taslak (360); önizleme yok, doğrudan kaydeder.
  const onSaveDraft = (v: MinutesFormValues) => {
    if (!validateContents()) return
    submitCreate(v, false)
  }

  const previewData: PreviewData | null = pending
    ? {
        name: pending.name,
        groupName:
          (groupOptions.data ?? []).find((g) => g.id === pending.meetingGroupId)?.name ?? '',
        date: formatDate(pending.meetingDate),
        time: `${pending.meetingStartTime} – ${pending.meetingEndTime}`,
        location: pending.meetingLocation,
        editorName,
        participants: participants.map((p) => ({
          fullName: p.fullName,
          employeeId: p.employeeId,
          jobTitle: p.jobTitleTr ?? p.jobTitle ?? '',
        })),
        actions: actionRows.map((r) => ({
          name: r.name,
          description: r.description,
          importance:
            actionLevels.data?.importanceLevels.find((l) => l.id === r.importanceLevelId)?.name ??
            '—',
          deadline: r.deadline ? formatDate(r.deadline) : '',
        })),
        // Basit içerikler — yalnız dolu satırlar (boşlar önizlemede gösterilmez).
        agenda: itemRows.agenda
          .filter((r) => r.name.trim() || r.description.trim())
          .map((r) => ({ name: r.name, description: r.description })),
        principles: itemRows.principles.filter((r) => r.name.trim()).map((r) => ({ name: r.name })),
        decisions: itemRows.decisions.filter((r) => r.name.trim()).map((r) => ({ name: r.name })),
      }
    : null

  // Önizlemeden İndir: PDF'i kaydetmeden üret + indir (publish alanı önizlemede önemsiz).
  const onDownload = async () => {
    if (!pending) return
    setDownloading(true)
    try {
      await downloadMinutesPreviewPdf(buildBody(pending, true))
    } catch {
      toast.error(t('minutes.preview.downloadError'))
    } finally {
      setDownloading(false)
    }
  }

  // Önizlemeden Yayınla: asıl kaydetme (create publish=true → 361 + backend PDF + dosyalar).
  const onPublish = () => {
    if (!pending) return
    submitCreate(pending, true)
  }

  return (
    <div className="page minutes-page">
      <PageHeader title={t('minutes.title')} />
      <form onSubmit={handleSubmit(onValid)} className="minutes-form" noValidate>
        {/* Temel Bilgiler kartı: bilgiler + Katılımcılar alt-başlık olarak içeride (item 3). */}
        <MeetingInfoSection control={control}>
          <ParticipantsSection
            participants={participants}
            onChange={handleParticipantsChange}
            error={participantError}
            excludeIds={distributionList.map((p) => p.employeeId)}
          />
          {/* Dağıtım listesi katılımcıların hemen altında (119). İki liste birbirini dışlar: bir kişi
              hem katılımcı hem dağıtım listesinde olamaz (sunucu da reddeder). */}
          <ParticipantsSection
            participants={distributionList}
            onChange={setDistributionList}
            i18nPrefix="minutes.distributionList"
            required={false}
            inputId="minutes-distribution-search"
            excludeIds={participants.map((p) => p.employeeId)}
          />
        </MeetingInfoSection>

        {/* İçerik listeleri (Toplantı Kayıt Tipi'nden dinamik). Aksiyon → aksiyon editörü; Gündem/Prensip/Karar
            → basit içerik editörü; tanımsız tip → görünür-pasif placeholder. */}
        {(sections.data ?? []).map((s) => {
          if (s.id === ACTION_DECISION_TYPE_ID) {
            return (
              <ActionsEditor
                key={s.id}
                title={contentSectionTitle(s, i18n.language)}
                value={actionRows}
                onChange={setActionRows}
                meetingDate={meetingDateVal}
                showNumber={false}
                // Satırlar burada YEREL: kaldırmak kaydedilmemiş formdan çıkarmaktır, görev
                // silmek değil (kayıt "Kaydet (taslak)"/"Yayınla" ile üretilir). Toplantı
                // Detayı bu prop'u VERMEZ — orada değişiklik anında sunucuya gider.
                removable
              />
            )
          }
          const kind = KIND_BY_DECISION_TYPE[s.id]
          if (kind) {
            return (
              <MinuteItemsEditor
                key={s.id}
                kind={kind}
                title={contentSectionTitle(s, i18n.language)}
                value={itemRows[kind]}
                onChange={(rows) => setKindRows(kind, rows)}
                showNumber={false}
                // Kayıt henüz yok: prensip kararı dosyaları yerelde bekler, kaydetme dönüşünde yüklenir.
                pendingFiles
                // Gündem'de sürükle-bırak: yerel dizi yeniden sıralanır; numara yayında bu sıraya göre atanır.
                onReorder={kind === 'agenda' ? (rows) => setKindRows(kind, rows) : undefined}
              />
            )
          }
          return (
            <DeferredSection
              key={s.id}
              title={contentSectionTitle(s, i18n.language)}
              description={t('minutes.deferred.desc')}
            />
          )
        })}

        <ExcelImportSection
          downloading={templateDownloading}
          uploading={excelImport.isPending}
          onDownloadTemplate={onDownloadTemplate}
          onFileSelected={onExcelFileSelected}
        />

        <DocumentsSection files={files} onChange={setFiles} />

        <div className="minutes-publish-bar">
          {/* Kaydet → taslak (360), önizleme yok. Yayınla → önizleme (submit) → 361. */}
          <Button
            type="button"
            variant="secondary"
            icon="pi pi-save"
            label={t('minutes.saveDraft')}
            onClick={handleSubmit(onSaveDraft)}
            disabled={publishing}
          />
          <Button
            type="submit"
            icon="pi pi-check"
            label={t('minutes.publish')}
            disabled={publishing}
          />
        </div>
      </form>

      <ExcelImportPreviewDialog
        visible={excelPreview != null}
        preview={excelPreview}
        hasExistingExcelRows={hasExcelRows}
        onCancel={() => setExcelPreview(null)}
        onTransfer={onExcelTransfer}
      />

      <TutanakPreviewDialog
        visible={pending != null}
        data={previewData}
        publishing={publishing}
        downloading={downloading}
        onCancel={() => (publishing ? undefined : setPending(null))}
        onDownload={onDownload}
        onPublish={onPublish}
      />
    </div>
  )
}
